/**
 * Application state (Preact signals) and derived measurement data.
 * Heavy derived values (planes, masks) are memoised `computed`s that only
 * recompute when their specific inputs change.
 */
import { batch, computed, signal } from '@preact/signals';
import type { ColormapId } from '../core/colormaps';
import { DEFAULT_METRICS, computeMetrics, derivative, type MetricSettings, type ProfileMetrics } from '../core/metrics';
import {
  chromaFromXyz,
  clipDetectionActive,
  computeClipMask,
  computePlane,
  DEFAULT_PIPELINE,
  isDerivedChroma,
  makeContext,
  quantityInfo,
  quantityUnit,
  type PipelineContext,
  type PipelineSettings,
  type QuantityId,
} from '../core/pipeline';
import { DEFAULT_SAMPLING, lineBounds, sampleProfile, type LineGeom, type Profile, type SamplingSettings } from '../core/sampling';
import { DEFAULT_SPECTRUM, type SpectrumSettings } from '../core/fft';
import { DEFAULT_SPATIAL, unitScale, type SpatialCalibration } from '../core/units';
import type { LineRoi, Roi } from '../core/roi';
import type { Plane, RawFrame } from '../core/types';
import { robustRange } from '../core/stats';
import { DEFAULT_RAW, type RawOptions } from '../io/decoders/raw';

export type SlotId = 'A' | 'B' | 'dark' | 'flat';
export const SLOT_LABELS: Record<SlotId, string> = {
  A: 'Image A (measured)',
  B: 'Image B (reference / compare)',
  dark: 'Dark frame',
  flat: 'Flat field',
};

export type ToolId = 'select' | 'pan' | 'line' | 'rect' | 'ellipse' | 'polygon' | 'point' | 'calibrate';
export type CompareMode = 'A' | 'B' | 'diff' | 'ratio' | 'pct';
export type DisplayMode = 'color' | 'gray' | 'false';

export interface ContourSettings {
  enabled: boolean;
  auto: boolean;
  count: number;
  logSpacing: boolean;
  levels: number[];
  color: string;
  labels: boolean;
}

export interface DisplaySettings {
  mode: DisplayMode;
  colormap: ColormapId;
  /** Number of discrete colour bands (0 = continuous). */
  bands: number;
  log: boolean;
  auto: boolean;
  /** Percentile clipped at each end by auto window. */
  autoPercentile: number;
  lo: number;
  hi: number;
  /** Exposure compensation (EV) for the colour view. */
  exposure: number;
  showClip: boolean;
  showGrid: boolean;
  showValues: boolean;
  showScaleBar: boolean;
  showLegend: boolean;
  showLabels: boolean;
  contours: ContourSettings;
}

export const DEFAULT_DISPLAY: DisplaySettings = {
  mode: 'color',
  colormap: 'turbo',
  bands: 0,
  log: false,
  auto: true,
  autoPercentile: 0.1,
  lo: 0,
  hi: 1,
  exposure: 0,
  showClip: false,
  showGrid: true,
  showValues: true,
  showScaleBar: true,
  showLegend: true,
  showLabels: true,
  contours: { enabled: false, auto: true, count: 8, logSpacing: false, levels: [], color: '#ffffff', labels: true },
};

export type EnvelopeMode = 'none' | 'sd' | 'minmax';

export interface ProfileView {
  envelope: EnvelopeMode;
  showDerivative: boolean;
  showAllLines: boolean;
  showB: boolean;
  showMarkers: boolean;
  showPinned: boolean;
  normalize: boolean;
  yLog: boolean;
  /** Fixed y-axis range (null = auto). */
  yMin: number | null;
  yMax: number | null;
}

export const DEFAULT_PROFILE_VIEW: ProfileView = {
  envelope: 'sd',
  showDerivative: false,
  showAllLines: true,
  showB: true,
  showMarkers: true,
  showPinned: true,
  normalize: false,
  yLog: false,
  yMin: null,
  yMax: null,
};

export interface PinnedProfile {
  id: string;
  label: string;
  color: string;
  /** Distance along the line in pixels (converted to the spatial unit when drawn). */
  x: Float64Array;
  y: Float64Array;
  unit: string;
}

export interface Notice {
  id: number;
  kind: 'info' | 'warn' | 'error' | 'success';
  text: string;
  action?: { label: string; run: () => void };
  timeout?: number;
}

// ------------------------------------------------------------------ primary state

export const frames = {
  A: signal<RawFrame | null>(null),
  B: signal<RawFrame | null>(null),
  dark: signal<RawFrame | null>(null),
  flat: signal<RawFrame | null>(null),
};
export const pipeline = signal<PipelineSettings>(structuredClone(DEFAULT_PIPELINE));
export const quantity = signal<QuantityId>('Y');
export const sampling = signal<SamplingSettings>({ ...DEFAULT_SAMPLING });
export const metricSettings = signal<MetricSettings>({ ...DEFAULT_METRICS, widthLevels: [...DEFAULT_METRICS.widthLevels] });
export const spectrumSettings = signal<SpectrumSettings>({ ...DEFAULT_SPECTRUM });
export const spatial = signal<SpatialCalibration>({ ...DEFAULT_SPATIAL });
export const display = signal<DisplaySettings>(structuredClone(DEFAULT_DISPLAY));
export const compareMode = signal<CompareMode>('A');
export const profileView = signal<ProfileView>({ ...DEFAULT_PROFILE_VIEW });
export const rawOptions = signal<RawOptions>({ ...DEFAULT_RAW });

export const rois = signal<Roi[]>([]);
export const selectedId = signal<string | null>(null);
export const tool = signal<ToolId>('select');
export const dragging = signal(false);
/** Image coordinates under the mouse (continuous). */
export const hover = signal<{ x: number; y: number } | null>(null);
/** Distance (px along the line) hovered in the profile plot. */
export const plotHover = signal<{ roiId: string; t: number } | null>(null);
export const pinned = signal<PinnedProfile[]>([]);
export const busy = signal<string | null>(null);
export const notices = signal<Notice[]>([]);
/** Selected MTF source ROI id (rect). */
export const mtfRoiId = signal<string | null>(null);

let noticeId = 0;
export function notify(kind: Notice['kind'], text: string, opts: { action?: Notice['action']; timeout?: number } = {}): number {
  const id = ++noticeId;
  const timeout = opts.timeout ?? (kind === 'error' ? 12000 : kind === 'warn' ? 9000 : 5000);
  notices.value = [...notices.value, { id, kind, text, action: opts.action, timeout }];
  if (timeout > 0) setTimeout(() => dismiss(id), timeout);
  return id;
}
export function dismiss(id: number) {
  notices.value = notices.value.filter((n) => n.id !== id);
}

export function updateSignal<T extends object>(s: { value: T }, patch: Partial<T>) {
  s.value = { ...s.value, ...patch };
}

// ------------------------------------------------------------------ derived data

const aux = computed(() => ({ dark: frames.dark.value, flat: frames.flat.value }));

export const ctxA = computed<PipelineContext | null>(() => {
  const f = frames.A.value;
  return f ? makeContext(f, pipeline.value, aux.value) : null;
});

export const ctxB = computed<PipelineContext | null>(() => {
  const f = frames.B.value;
  if (!f) return null;
  const a = aux.value;
  // Auxiliary frames only apply when they match B's size.
  const fit = (x: RawFrame | null) => (x && x.width === f.width && x.height === f.height ? x : null);
  return makeContext(f, pipeline.value, { dark: fit(a.dark), flat: fit(a.flat) });
});

export const clipMaskA = computed(() => {
  const f = frames.A.value;
  return f && clipDetectionActive(f, pipeline.value.clipDetection) ? computeClipMask(f, pipeline.value.floatClipLevel) : null;
});
export const clipMaskB = computed(() => {
  const f = frames.B.value;
  return f && clipDetectionActive(f, pipeline.value.clipDetection) ? computeClipMask(f, pipeline.value.floatClipLevel) : null;
});

export const planeA = computed<Plane | null>(() => (ctxA.value ? computePlane(ctxA.value, quantity.value) : null));
export const planeB = computed<Plane | null>(() => (ctxB.value ? computePlane(ctxB.value, quantity.value) : null));

export interface Measurement {
  plane: Plane;
  ctx: PipelineContext;
  clip: Uint8Array | null;
  unit: string;
  axis: string;
  label: string;
  mode: CompareMode;
  /** Chromaticity-type quantities are derived from band-averaged XYZ. */
  derivedChroma: boolean;
  error?: string;
}

/** The plane every measurement reads from (A, B, or an A/B comparison). */
export const measurement = computed<Measurement | null>(() => {
  const mode = compareMode.value;
  const q = quantity.value;
  const qi = quantityInfo(q);
  const a = ctxA.value;
  const b = ctxB.value;
  const base = (ctx: PipelineContext, plane: Plane, clip: Uint8Array | null, m: CompareMode): Measurement => {
    const u = quantityUnit(q, ctx);
    return { plane, ctx, clip, unit: u.unit, axis: u.axis, label: qi.label, mode: m, derivedChroma: isDerivedChroma(q) };
  };
  if (mode === 'B' && b && planeB.value) return base(b, planeB.value, clipMaskB.value, 'B');
  if (!a || !planeA.value) return b && planeB.value ? base(b, planeB.value, clipMaskB.value, 'B') : null;
  if (mode === 'A' || mode === 'B') {
    const m = base(a, planeA.value, clipMaskA.value, 'A');
    if (mode === 'B') m.error = 'No image B loaded; showing A';
    return m;
  }
  const pa = planeA.value;
  const pb = planeB.value;
  if (!pb || pb.width !== pa.width || pb.height !== pa.height) {
    const m = base(a, pa, clipMaskA.value, 'A');
    m.error = pb ? 'A and B differ in size: comparison unavailable' : 'Load image B to compare';
    return m;
  }
  const out = new Float32Array(pa.data.length);
  for (let i = 0; i < out.length; i++) {
    const x = pa.data[i];
    const y = pb.data[i];
    out[i] = mode === 'diff' ? x - y : mode === 'ratio' ? x / y : ((x - y) / y) * 100;
  }
  const ma = clipMaskA.value;
  const mb = clipMaskB.value;
  const clip = ma && mb ? ma.map((v, i) => v | mb[i]) : ma;
  const u = quantityUnit(q, a);
  const label = mode === 'diff' ? `${qi.short} A − B` : mode === 'ratio' ? `${qi.short} A / B` : `${qi.short} (A − B) / B`;
  const unit = mode === 'diff' ? u.unit : mode === 'ratio' ? '' : '%';
  return {
    plane: { ...pa, data: out },
    ctx: a,
    clip,
    unit,
    axis: unit ? `${label} [${unit}]` : label,
    label,
    mode,
    derivedChroma: false,
  };
});

export const imageSize = computed(() => {
  const f = frames.A.value ?? frames.B.value;
  return f ? { w: f.width, h: f.height } : null;
});

/** Only the percentile: a computed number, so unrelated display changes do not propagate. */
const autoPercentile = computed(() => display.value.autoPercentile);

/** Robust display range of the measurement plane (recomputed only when the plane or percentile changes). */
export const autoRange = computed<[number, number]>(() => {
  const m = measurement.value;
  if (!m) return [0, 1];
  return robustRange(m.plane.data, autoPercentile.value);
});

export const windowRange = computed<[number, number]>(() => {
  const d = display.value;
  let [lo, hi] = d.auto ? autoRange.value : [d.lo, d.hi];
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) [lo, hi] = autoRange.value;
  if (hi < lo) [lo, hi] = [hi, lo];
  // Log scaling needs a positive lower limit.
  if (d.log && !(lo > 0)) lo = hi > 0 ? hi * 1e-4 : Number.MIN_VALUE;
  // An empty window would divide by zero in the shader.
  if (!(hi > lo)) hi = lo + (Math.abs(lo) || 1) * 1e-6;
  return [lo, hi];
});

export const lineRois = computed(() => rois.value.filter((r): r is LineRoi => r.kind === 'line'));
export const selectedRoi = computed(() => rois.value.find((r) => r.id === selectedId.value) ?? null);
export const selectedLine = computed<LineRoi | null>(() => {
  const s = selectedRoi.value;
  if (s?.kind === 'line') return s;
  return lineRois.value[lineRois.value.length - 1] ?? null;
});

export function geomOf(r: LineRoi): LineGeom {
  return { x0: r.x0, y0: r.y0, x1: r.x1, y1: r.y1, width: r.width };
}

/** Profile of one line on a given measurement (chromaticity quantities via band-averaged XYZ). */
export function sampleLine(m: Measurement, r: LineRoi, s: SamplingSettings): Profile {
  const g = geomOf(r);
  if (!m.derivedChroma) return sampleProfile(m.plane, g, s, m.clip);
  const box = lineBounds(g);
  const ss = { ...s, stat: 'mean' as const };
  const [X, Y, Z] = (['X', 'Y', 'Z'] as const).map((c) => sampleProfile(computePlane(m.ctx, c, box), g, ss, m.clip));
  const q = quantity.value;
  const v = new Float64Array(Y.n);
  for (let i = 0; i < Y.n; i++) v[i] = chromaFromXyz(q, X.v[i], Y.v[i], Z.v[i]);
  return { ...Y, v, sd: new Float64Array(Y.n).fill(NaN), lo: new Float64Array(Y.n).fill(NaN), hi: new Float64Array(Y.n).fill(NaN) };
}

export interface LineResult {
  roi: LineRoi;
  profile: Profile;
  metrics: ProfileMetrics;
  derivative: Float64Array;
  /** Profile of image B along the same line (when B is loaded and compare mode is A). */
  profileB: Profile | null;
}

export const lineResults = computed<Map<string, LineResult>>(() => {
  const out = new Map<string, LineResult>();
  const m = measurement.value;
  if (!m) return out;
  const s = sampling.value;
  const ms = metricSettings.value;
  const scale = unitScale(spatial.value);
  const settings = { ...ms, minSeparation: ms.minSeparation / scale };
  const b = ctxB.value;
  const pb = planeB.value;
  for (const r of lineRois.value) {
    const profile = sampleLine(m, r, s);
    const metrics = computeMetrics(profile.t, profile.v, settings, profile.clip);
    let profileB: Profile | null = null;
    if (m.mode === 'A' && b && pb) {
      const mb: Measurement = { ...m, plane: pb, ctx: b, clip: clipMaskB.value };
      profileB = sampleLine(mb, r, s);
    }
    out.set(r.id, { roi: r, profile, metrics, derivative: derivative(profile.t, profile.v, ms.derivativeSmoothing), profileB });
  }
  return out;
});

export const selectedResult = computed(() => {
  const l = selectedLine.value;
  return l ? (lineResults.value.get(l.id) ?? null) : null;
});

// ------------------------------------------------------------------ ROI history (undo / redo)

const undoStack: Roi[][] = [];
const redoStack: Roi[][] = [];
export const historyVersion = signal(0);

/** Record the current ROI list before a change (call once per user action). */
export function snapshotRois() {
  undoStack.push(rois.value);
  if (undoStack.length > 200) undoStack.shift();
  redoStack.length = 0;
  historyVersion.value++;
}

export function undo() {
  const prev = undoStack.pop();
  if (!prev) return;
  redoStack.push(rois.value);
  batch(() => {
    rois.value = prev;
    if (selectedId.value && !prev.some((r) => r.id === selectedId.value)) selectedId.value = null;
    historyVersion.value++;
  });
}

export function redo() {
  const next = redoStack.pop();
  if (!next) return;
  undoStack.push(rois.value);
  batch(() => {
    rois.value = next;
    historyVersion.value++;
  });
}

export const canUndo = computed(() => (historyVersion.value, undoStack.length > 0));
export const canRedo = computed(() => (historyVersion.value, redoStack.length > 0));
